import { useCallback, useState } from "react";

/**
 * Which calculations are running, and against which inputs (operator, task
 * 4553).
 *
 * Calculate used to be locked for as long as ANY run was in flight, so a
 * reader who changed a filter mid-run had to wait out a run for the filters
 * they had just abandoned. The lock now covers only the inputs on screen: a
 * run is recorded under the key of the inputs it was started with, and
 * Calculate stays inactive only while the CURRENT inputs' key is among them.
 * Changing a filter re-enables it; changing it back to a running run's inputs
 * disables it again, so the reader cannot start a duplicate of a run already
 * on its way.
 *
 * Several runs may overlap, and every one of them applies its results in the
 * order it finishes -- the operator's call: nothing here discards a run. The
 * keys are a list rather than a set so that two runs against the same inputs
 * (one finished between them is enough) each hold their own place.
 */
export const useInFlightCalculations = () => {
  const [inFlight, setInFlight] = useState<readonly string[]>([]);

  // Records a run against `key` and hands back the call that retires it --
  // ONE occurrence, so a second run under the same key keeps the lock, and
  // only once, so a repeated call cannot retire that other run.
  const start = useCallback((key: string) => {
    setInFlight((keys) => [...keys, key]);
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      setInFlight((keys) => {
        const index = keys.indexOf(key);
        return index < 0
          ? keys
          : [...keys.slice(0, index), ...keys.slice(index + 1)];
      });
    };
  }, []);

  const isCalculatingInputs = useCallback(
    (key: string) => inFlight.includes(key),
    [inFlight],
  );

  return {
    start,
    isCalculatingInputs,
    isCalculating: inFlight.length > 0,
  };
};
